/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { cssMap, jsx } from '@atlaskit/css';
import Button from '@atlaskit/button/default/button';
import { aiSuggestionsMessages } from '@atlaskit/editor-common/ai-messages';
import Heading from '@atlaskit/heading/heading';
import Image from '@atlaskit/image/image';
import TextReviewIcon from '@atlaskit/icon-lab/core/text-review';
import { Text } from '@atlaskit/primitives/compiled/text';
import { token } from '@atlaskit/tokens';

import { type FullPageEditorPresetAPI } from '@confluence/full-page-editor-store';

import { SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT } from '../analytics.ts';
import { useIsOffline } from '../useIsOffline.ts';

import InitialSuggestions from './assets/InitialSuggestions.svg';
import EmptySuggestionsDark from './assets/EmptySuggestionsDark.svg';
import EmptySuggestionsDoneDark from './assets/EmptySuggestionsDoneDark.svg';
import EmptySuggestionsDoneLight from './assets/EmptySuggestionsDoneLight.svg';
import EmptySuggestionsLight from './assets/EmptySuggestionsLight.svg';

const styles = cssMap({
	container: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		justifyContent: 'center',
		gap: token('space.200'),
		boxSizing: 'border-box',
		minHeight: '100%',
		paddingTop: token('space.300'),
		paddingBottom: token('space.300'),
		paddingLeft: token('space.300'),
		paddingRight: token('space.300'),
	},
	outcome: {
		gap: token('space.250'),
	},
	content: {
		display: 'flex',
		flexDirection: 'column',
		alignItems: 'center',
		gap: token('space.250'),
		width: '100%',
	},
	text: {
		display: 'flex',
		flexDirection: 'column',
		gap: token('space.150'),
		width: '100%',
		textAlign: 'center',
		overflowWrap: 'anywhere',
	},
});
const i18n = defineMessages({
	initialHeading: {
		id: 'suggestions-panel.empty-state.initial.heading',
		defaultMessage: 'Better work, in less time',
		description: 'Heading inviting authors to review their content for suggestions',
	},
	initialDescription: {
		id: 'suggestions-panel.empty-state.initial.description',
		defaultMessage: 'Get suggestions to improve your writing, structure, and accuracy.',
		description: 'Description of what the content review checks',
	},
	noResultsHeading: {
		id: 'suggestions-panel.empty-state.no-results.heading',
		defaultMessage: 'No suggestions found',
		description: 'Heading after a successful review finds no pending suggestions',
	},
	noResultsDescription: {
		id: 'suggestions-panel.empty-state.no-results.content-reviewer-description',
		defaultMessage: 'Content Reviewer checked the writing, structure, and accuracy.',
		description: 'Explanation after a successful review finds no pending suggestions',
	},
	resolvedHeading: {
		id: 'suggestions-panel.empty-state.resolved.heading',
		defaultMessage: 'All suggestions resolved',
		description: 'Heading after the author accepts or discards the final pending suggestion',
	},
	resolvedDescription: {
		id: 'suggestions-panel.empty-state.resolved.description',
		defaultMessage: 'Review again when you’ve made edits to get new suggestions.',
		description: 'Invitation to review after resolving all pending suggestions',
	},
	reviewAgain: {
		id: 'suggestions-panel.empty-state.review-again',
		defaultMessage: 'Review again',
		description: 'Button to start another content review after finding no suggestions',
	},
});

export type SuggestionsEmptyStateVariant = 'initial' | 'noSuggestionsResult' | 'allResolved';

const presentations = {
	initial: {
		heading: i18n.initialHeading,
		description: i18n.initialDescription,
		image: InitialSuggestions,
		darkImage: InitialSuggestions,
		width: 109.5,
	},
	noSuggestionsResult: {
		heading: i18n.noResultsHeading,
		description: i18n.noResultsDescription,
		image: EmptySuggestionsLight,
		darkImage: EmptySuggestionsDark,
		width: 72,
	},
	allResolved: {
		heading: i18n.resolvedHeading,
		description: i18n.resolvedDescription,
		image: EmptySuggestionsDoneLight,
		darkImage: EmptySuggestionsDoneDark,
		width: 72,
	},
};

type SuggestionsEmptyStateProps = {
	api?: FullPageEditorPresetAPI;
	variant?: SuggestionsEmptyStateVariant;
};

export const SuggestionsEmptyState = ({
	api,
	variant = 'initial',
}: SuggestionsEmptyStateProps): React.JSX.Element => {
	const { formatMessage } = useIntl();
	const isOffline = useIsOffline(api);
	const handleClick = useCallback(() => {
		if (isOffline) {
			return;
		}
		api?.aiSuggestions?.actions?.setThinkingState('thinking', {
			entryPoint: SUGGESTIONS_PANEL_EMPTY_STATE_ENTRY_POINT,
		});
	}, [api, isOffline]);

	const presentation = presentations[variant];

	return (
		<div css={[styles.container, variant !== 'initial' && styles.outcome]}>
			<Image
				src={presentation.image}
				srcDark={presentation.darkImage}
				width={presentation.width}
				alt=""
			/>
			<div css={styles.content}>
				<div css={styles.text}>
					<Heading as="h2" size="small">
						{formatMessage(presentation.heading)}
					</Heading>
					<Text as="p" size="medium" align="center">
						{formatMessage(presentation.description)}
					</Text>
				</div>
				<Button iconBefore={TextReviewIcon} onClick={handleClick} isDisabled={isOffline}>
					{formatMessage(
						variant === 'noSuggestionsResult'
							? i18n.reviewAgain
							: aiSuggestionsMessages.suggestEditsButtonLabel,
					)}
				</Button>
			</div>
		</div>
	);
};
